// External modules
import React from 'react';
import universalCookie from 'universal-cookie';
import moment from 'moment';
import { Redirect } from 'react-router-dom';

import { MuiThemeProvider, createTheme } from '@material-ui/core/styles';

import acHTTPClient from 'services/ACHTTPClient';

// Internal modules
import Router from './Router';
import { getNevigationItems } from './components/common/global';

import { ReactComponent as ACIcon } from './medias/Archi.svg';
import { ReactComponent as ACWhiteIcon } from './medias/amazingco-white-icon.svg';
import { ReactComponent as GoogleLogo } from './medias/google-logo.svg';
import { LicenseInfo } from '@mui/x-license-pro';
import config from 'config/config';

LicenseInfo.setLicenseKey(config.muiProLicenseKey);

const cookies = new universalCookie();

const theme = createTheme({
    
    typography: {
        body1 : { fontSize : '14px'},
        body2 : { fontSize : '12px'},
        fontFamily: [
            "gilroy-regular",
            "-apple-system",
            "BlinkMacSystemFont",
            "Segoe UI","Roboto","Oxygen","Ubuntu","Cantarell","Fira Sans","Droid Sans","Helvetica Neue",
            "sans-serif"
        ].join(','),
      },
          
    palette: {
        primary: {
            light: '#52A8EF',
            main: '#52A8EF',
            dark: '#52A8EF',
        },
        secondary: {
            main: '#FF765F',
        },
    },
});

const setAuth = function setAuthHTTPClientHeader(token) {
    if (token) {
        acHTTPClient.withAuthorizationHeader(`${token.tokenType} ${token.accessToken}`);
    }
};

const forceLogOut = function forceLogOut(key, hasAlert = false) {
    if (key) {
        cookies.remove(key);
        if (hasAlert) {
            window.alert('You have been logged out from the website');
        }
        window.location.replace('/');
    }
};

class App extends React.Component {
    constructor(props) {
        super(props);

        const { config } = props;
        const cookiesConfig = config.cookies;
        const encodedLoggedInUser = cookies.get(cookiesConfig.authLoggedInUser) || null;
        let loggedInUser;

        if (encodedLoggedInUser) {
            try {
                loggedInUser = JSON.parse(encodedLoggedInUser);
            } catch (err) {
                loggedInUser = encodedLoggedInUser;
            }

            const diffInSeconds = loggedInUser.expiryTimestamp - moment().unix();

            if (loggedInUser.oauth2Token && loggedInUser.oauth2Token.accessToken) {
                const token = {
                    accessToken: loggedInUser.oauth2Token.accessToken,
                    tokenType: loggedInUser.oauth2Token.tokenType || 'Bearer',
                    expirySecond: diffInSeconds,
                    scope: loggedInUser.oauth2Token.scope || '',
                };
                setAuth(token);
            }

            // Get AmazingCo user data
            if (loggedInUser.emailAddress) {
                this.setUserByEmailAddress(loggedInUser.emailAddress);
            }

            if (diffInSeconds > 0) {
                console.log(`Setting timer to log out in ${diffInSeconds} seconds`);
                setTimeout(function () {
                    // Log user out
                    forceLogOut(cookiesConfig.authLoggedInUser, true);
                }, diffInSeconds * 1000);
            } else {
                forceLogOut(cookiesConfig.authLoggedInUser);
            }
        }

        // 'loggedInUser' for auth
        // 'user' for amazingco user data
        this.state = {
            error: null,
            loggedInUser: loggedInUser,
            selectedNavigationItem: getNevigationItems()[0],
            user: null,
        };

        this.onChangeErrorMessage = this.onChangeErrorMessage.bind(this);
        this.onChangeLoggedInUser = this.onChangeLoggedInUser.bind(this);
        this.onChangeNavigationItem = this.onChangeNavigationItem.bind(this);
        this.onChangeUser = this.onChangeUser.bind(this);
        this.onRedirectToLoggedInPage = this.onRedirectToLoggedInPage.bind(this);

        this.getIsEmployee = this.getIsEmployee.bind(this);
        this.setUserByEmailAddress = this.setUserByEmailAddress.bind(this);
    }

    onChangeErrorMessage(error) {
        if (!error) {
            this.setState({
                errorMessage: null,
            });
            return;
        }

        let em = error;
        // JavaScript error object: err.message
        if (error.message) em = error.message;
        // Auth0 error: err.description
        if (error.description) em = error.description;

        if (this.state.errorMessage !== em) {
            console.error('errorMessage', em);

            this.setState({
                errorMessage: em,
            });
        }
    }

    onChangeLoggedInUser(loggedInUser) {
        const { auth, config } = this.props;
        const cookiesConfig = config.cookies;
        if (!auth || !config || !cookiesConfig) return;

        if (loggedInUser) {
            const expiresInSeconds = loggedInUser.expiresInSeconds || config.authWebExpiryInSeconds;

            if (loggedInUser.oauth2Token && loggedInUser.oauth2Token.accessToken) {
                const token = {
                    accessToken: loggedInUser.oauth2Token.accessToken,
                    tokenType: loggedInUser.oauth2Token.tokenType || 'Bearer',
                    expirySecond: expiresInSeconds,
                    scope: loggedInUser.oauth2Token.scope || '',
                };
                setAuth(token);
            }

            cookies.set(cookiesConfig.authLoggedInUser, JSON.stringify(loggedInUser), {
                path: '/',
                expires: moment().add(expiresInSeconds, 'seconds').toDate(),
            });

            if (expiresInSeconds > 0) {
                console.log(`Setting timer to log out in ${expiresInSeconds} seconds`);
                setTimeout(function () {
                    // Log user out
                    forceLogOut(cookiesConfig.authLoggedInUser, true);
                }, expiresInSeconds * 1000);
            }
        } else {
            console.log('logging out...');

            const authConfig = auth.getAuthConfig();
            if (!authConfig || !authConfig.web) return;

            const webAuthConfig = authConfig.web;

            const webAuth = auth.getWebAuth();
            if (!webAuth) return;

            webAuth.logout({
                returnTo: webAuthConfig.logoutURL,
                clientID: authConfig.id,
            });
            forceLogOut(cookiesConfig.authLoggedInUser);
        }

        this.setState({
            loggedInUser,
        });
    }

    onChangeNavigationItem(navigationItem) {
        if (navigationItem) {
            // console.log('navigationItem', navigationItem);
            this.setState({
                selectedNavigationItem: navigationItem,
            });
        }
    }

    onChangeUser(user) {
        // console.log('user', user);
        const newState = { user };
        if (user.detail && user.detail.providerId) {
            // console.log('user.detail.providerId', user.detail.providerId);
            newState.providerId = user.detail.providerId;
        }
        this.setState(newState);
    }

    onRedirectToLoggedInPage() {
        return <Redirect to="/" />;
    }

    // Check if user/loggedin User is employee
    getIsEmployee() {
        const { loggedInUser, user } = this.state;

        let isEmployee = false;
        if (user && user.appMetadata && user.appMetadata.subject && user.appMetadata.subject.types) {
            isEmployee = user.appMetadata.subject.types.includes('employee');
        } else if (loggedInUser && loggedInUser.types) {
            isEmployee = loggedInUser.types.includes('employee');
        }
        // console.log('isEmployee', isEmployee);

        return isEmployee;
    }

    // Get AmazingCo user data
    setUserByEmailAddress(emailAddress) {
        if (!emailAddress) {
            return;
        }

        const { auth } = this.props;

        const authConfig = auth.getAuthConfig();
        if (!authConfig || !authConfig.web) return;

        const webAuthConfig = authConfig.web;
        if (!webAuthConfig || !webAuthConfig.userAPIURL) return;

        const getUserURL = `${webAuthConfig.userAPIURL}/api/v1/users/email-address/${emailAddress}`;
        try {
            acHTTPClient
                .request({
                    method: 'GET',
                    url: getUserURL,
                })
                .then((getUserResponse) => {
                    if (getUserResponse && getUserResponse.data && getUserResponse.data.data) {
                        const getUser = getUserResponse.data.data;
                        console.log('getUser', getUser);
                        const user = { ...getUser };
                        if (getUser.detail) {
                            if (getUser.detail.dateOfBirth) {
                                try {
                                    user.detail.dateOfBirth = moment(getUser.detail.dateOfBirth).format('YYYY-MM-DD');
                                } catch (error) {
                                    console.log('Failed converting DoB, Error:', error);
                                }
                            }
                        }

                        this.onChangeUser(user);
                    } else {
                        console.log('Failed getting user, Response:', getUserResponse);
                    }
                })
                .catch((error) => {
                    console.log('Failed getting user, Error:', error);
                });
        } catch (error) {
            console.log('Failed getting user, Error:', error);
        }
    }

    render() {
        const { errorMessage, loggedInUser, selectedNavigationItem, user } = this.state;
        const { auth, config } = this.props;
        if (!auth || !config) return null;

        const authProperties = {
            auth,
            isSignUpAllowed: true,
            loggedInPath: '/',
            loggedInUser,
            onChangeLoggedInUserFunc: this.onChangeLoggedInUser,
            onChangeUserFunc: this.onChangeUser,
            onRedirectToLoggedInPageFunc: this.onRedirectToLoggedInPage,
            setUserByEmailAddressFunc: this.setUserByEmailAddress,
            socialAuths: [
                {
                    key: 'google',
                    Icon: GoogleLogo,
                },
            ],
            user,
        };

        const navigationProperties = {
            logo: ACIcon,
            mobileLogo: ACWhiteIcon,
            navigationItems: getNevigationItems(),
            onChangeNavigationItemFunc: this.onChangeNavigationItem,
            referenceItems: [
                {
                    text: 'HQ',
                    url: 'https://hq.amazingco.me',
                },
                {
                    text: 'Experience HQ',
                    url: 'http://aex-hq.amazingco.me',
                },
                {
                    text: 'Product HQ',
                    url: 'http://product-ui.amazingco.me',
                },
                {
                    text: 'Giftcard HQ',
                    url: 'http://giftcard-ui.amazingco.me',
                },
                {
                    text: 'Promotion HQ',
                    url: 'http://promo-ui.amazingco.me',
                },
            ],
            selectedNavigationItem,
            title: 'Experiences',
        };

        const errorProperties = {
            errorMessage,
            onChangeErrorMessageFunc: this.onChangeErrorMessage,
        };

        return (
            <MuiThemeProvider theme={theme}>
                <Router
                    authProperties={authProperties}
                    config={config}
                    navigationProperties={navigationProperties}
                    errorProperties={errorProperties}
                />
            </MuiThemeProvider>
        );
    }
}

export default App;
